import { useState, type FormEvent } from 'react';
import {
  useContractorPayments,
  useCreateContractorPayment,
  useUpdateContractorPayment,
  type PaymentMethod,
  type PaymentStatus,
} from '../api/queries/contractor-payments';
import { useContractors } from '../api/queries/contractors';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { MoneyInput } from '../components/MoneyInput';
import { LookupSelect } from '../components/LookupSelect';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useLookupLabel } from '../api/queries/lookups';
import './ContractorPaymentsPage.css';

type PaymentSortKey =
  | 'date'
  | 'contractor'
  | 'project'
  | 'method'
  | 'reference'
  | 'amount'
  | 'status'
  | 'tax_year';

import { formatMoney } from '../utils/money';

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

export function ContractorPaymentsPage(): JSX.Element {
  const contractorsQuery = useContractors({});
  const [contractorFilter, setContractorFilter] = useState<number | ''>('');
  const { data, isPending, error } = useContractorPayments(
    contractorFilter === '' ? null : contractorFilter,
  );
  const createPayment = useCreateContractorPayment();
  const updatePayment = useUpdateContractorPayment();
  const methodLabel = useLookupLabel('payment_method');
  const statusLabel = useLookupLabel('payment_status');

  const [showNew, setShowNew] = useState(false);
  const [newContractor, setNewContractor] = useState<number | ''>('');
  const [newAmount, setNewAmount] = useState('');
  const [newDate, setNewDate] = useState<number | null>(() => todayEpoch());
  const [newMethod, setNewMethod] = useState<PaymentMethod>('check');
  const [newReference, setNewReference] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newStatus, setNewStatus] = useState<PaymentStatus>('paid');
  const [createError, setCreateError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newContractor === '' || newAmount.trim().length === 0) return;
    if (newDate === null) {
      setCreateError('Pick a payment date.');
      return;
    }
    try {
      await createPayment.mutateAsync({
        contractor_id: newContractor,
        amount: newAmount.trim(),
        payment_date: newDate,
        method: newMethod,
        reference_number: newReference.trim() || undefined,
        notes: newNotes.trim() || undefined,
        status: newStatus,
      });
      setShowNew(false);
      setNewContractor('');
      setNewAmount('');
      setNewReference('');
      setNewNotes('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to record contractor payments.");
        return;
      }
      setCreateError('Could not save payment. Try again.');
    }
  };

  const payments = data?.contractor_payments ?? [];
  const contractors = contractorsQuery.data?.contractors ?? [];
  const sort = useClientSort(payments, (p, key: PaymentSortKey) => {
    switch (key) {
      case 'date': return p.payment_date;
      case 'contractor': return p.contractor_legal_name ?? `#${p.contractor_id}`;
      case 'project': return p.project_case_number ?? '';
      case 'method': return methodLabel(p.method);
      case 'reference': return p.reference_number ?? '';
      case 'amount': return Number(p.amount);
      case 'status': return statusLabel(p.status);
      case 'tax_year': return p.tax_year;
      default: return '';
    }
  }, { key: 'date', dir: 'desc' });

  if (isPending && contractorFilter !== '') return <p className="cp-status">Loading payments…</p>;
  if (error) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have permission to view contractor payments."
      : 'Failed to load payments.';
    return <p className="cp-status cp-error">{msg}</p>;
  }

  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + Number(p.amount), 0);
  const totalPending = payments
    .filter((p) => p.status === 'pending')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  return (
    <section className="page-stack page-stack-wide">
      <div className="card cp-toolbar">
      <header className="cp-header">
        <h2>Contractor Payments</h2>
        <div className="cp-header-controls">
          <select
            className="form-input cp-filter"
            value={contractorFilter}
            onChange={(e) => {
              const v = e.target.value;
              setContractorFilter(v === '' ? '' : Number(v));
            }}
          >
            <option value="">All contractors</option>
            {contractors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}{c.business_name ? ` (${c.business_name})` : ''}
              </option>
            ))}
          </select>
          {can('contractor.pay') && !showNew && (
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => setShowNew(true)}
            >
              Record payment
            </button>
          )}
        </div>
      </header>
      </div>

      <div className="card">
        <div className="cp-summary">
          <div className="cp-summary-card">
            <div className="cp-summary-label">Paid</div>
            <div className="cp-summary-value cp-summary-success">{formatMoney(totalPaid.toFixed(2))}</div>
          </div>
          {totalPending > 0 && (
            <div className="cp-summary-card">
              <div className="cp-summary-label">Pending</div>
              <div className="cp-summary-value cp-summary-warn">{formatMoney(totalPending.toFixed(2))}</div>
            </div>
          )}
          <div className="cp-summary-card">
            <div className="cp-summary-label">Records</div>
            <div className="cp-summary-value">{payments.length}</div>
          </div>
        </div>
      </div>

      {showNew && (
        <div className="card">
        <form className="cp-form" onSubmit={onCreate}>
          <div className="cp-form-row">
            <label className="cp-field">
              <span>Contractor *</span>
              <select
                className="form-input"
                value={newContractor}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewContractor(v === '' ? '' : Number(v));
                }}
                required
                disabled={createPayment.isPending}
              >
                <option value="">Choose…</option>
                {contractors.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name}</option>
                ))}
              </select>
            </label>
            <label className="cp-field">
              <span>Amount *</span>
              <MoneyInput
                value={newAmount}
                onChange={setNewAmount}
                placeholder="2500.00"
                required
                disabled={createPayment.isPending}
              />
            </label>
            <label className="cp-field">
              <span>Payment date *</span>
              <DateInput
                value={newDate}
                onChange={setNewDate}
                required
                disabled={createPayment.isPending}
              />
            </label>
          </div>
          <div className="cp-form-row">
            <label className="cp-field">
              <span>Method *</span>
              <LookupSelect
                category="payment_method"
                value={newMethod}
                onChange={setNewMethod}
                className="form-input"
                disabled={createPayment.isPending}
              />
            </label>
            <label className="cp-field">
              <span>Status *</span>
              <LookupSelect
                category="payment_status"
                value={newStatus}
                onChange={setNewStatus}
                className="form-input"
                disabled={createPayment.isPending}
              />
            </label>
            <label className="cp-field">
              <span>Reference #</span>
              <input
                type="text"
                className="form-input"
                value={newReference}
                onChange={(e) => setNewReference(e.target.value)}
                placeholder="check #, transfer ID"
                maxLength={120}
                disabled={createPayment.isPending}
              />
            </label>
          </div>
          <label className="cp-field cp-field-wide">
            <span>Notes</span>
            <input
              type="text"
              className="form-input"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              maxLength={10_000}
              disabled={createPayment.isPending}
            />
          </label>
          {createError && <p className="cp-error-banner" role="alert">{createError}</p>}
          <div className="cp-form-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                setCreateError(null);
              }}
              disabled={createPayment.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={createPayment.isPending || newContractor === '' || newAmount.trim().length === 0}
            >
              {createPayment.isPending ? 'Saving…' : 'Save payment'}
            </button>
          </div>
        </form>
        </div>
      )}

      {payments.length === 0 ? (
        <div className="card">
          <div className="cp-empty">
            <p>{contractorFilter === '' ? 'Select a contractor to view their payment history.' : 'No payments recorded for this contractor.'}</p>
            {can('contractor.pay') && contractorFilter !== '' && (
              <p className="cp-empty-meta">Click "Record payment" to log one.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-tight cp-table-card table-scroll-card">
        <TableScroll id="contractor-payments">
        <table className="cp-table">
          <thead>
            <tr>
              <SortableTh column="date" label="Date" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="contractor" label="Contractor" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="project" label="Project" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="method" label="Method" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="reference" label="Reference" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="amount" label="Amount" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="cp-numeric" />
              <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="tax_year" label="Tax year" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((p) => (
              <tr key={p.id}>
                <td>{fmtDate(p.payment_date)}</td>
                <td>{p.contractor_legal_name ?? `#${p.contractor_id}`}</td>
                <td>{p.project_case_number ? <code>{p.project_case_number}</code> : '—'}</td>
                <td>{methodLabel(p.method)}</td>
                <td>{p.reference_number ?? '—'}</td>
                <td className="cp-numeric cp-amount">{formatMoney(p.amount)}</td>
                <td>
                  {can('contractor.pay') ? (
                    <LookupSelect
                      category="payment_status"
                      value={p.status}
                      onChange={(v) => updatePayment.mutate({ id: p.id, status: v })}
                      className={`cp-status-select cp-status-${p.status}`}
                      disabled={updatePayment.isPending}
                    />
                  ) : (
                    <span className={`cp-pill cp-pill-${p.status}`}>{statusLabel(p.status)}</span>
                  )}
                </td>
                <td>{p.tax_year}</td>
              </tr>
            ))}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
